import React, { FC, useState, useMemo } from 'react';
import { Box, Tabs, Tab } from '@mui/material';

import { IDynamicComponent, IName, IComponentParams } from 'plugins/api/types';
import { EditPanel, ListPanel } from 'plugins/components/containers';
import { WiseTable } from 'components/wise';
import { PageTitle } from 'components/controls';
import { dynamicComponentToWiseTableProps } from 'plugins/pages/DynamicPage/adapters/dynamicComponentToWiseTableProps';

import 'plugins/theme/components/TabPanel/TabPanel.scss';

const tabsSx = {
  width: 'fit-content',
  borderTopLeftRadius: 8,
  borderTopRightRadius: 8,
  overflow: 'hidden',
  border: '1px solid #e0e0e0',
  borderBottom: '1px solid rgba(0, 0, 0, 0.1)',
  '& .MuiButtonBase-root.MuiTab-root': {
    backgroundColor: '#ffffff',
    textTransform: 'none' as const
  },
  '& .MuiButtonBase-root.MuiTab-root.Mui-selected': {
    fontWeight: 600,
    backgroundColor: '#f3f4f7'
  }
};

// typ danych wejściowych
interface IProps {
  title: string;
  names?: IName[];
  components?: IDynamicComponent[];
  pageSymbol: string;
  requestParams?: object;
  componentParams?: IComponentParams;
}

const TabPanel: FC<IProps> = ({
  title,
  names,
  components,
  pageSymbol,
  requestParams,
  componentParams
}) => {
  // lista tabów
  const tabs =
    names?.map((name: IName) => ({
      key: name.component_symbol,
      label: name.name
    })) || [];

  // rodzaj aktyenej listy
  const [activeTab, setActiveTab] = useState<string>(tabs?.[0].key || '');

  const activeIndex = useMemo(() => {
    const i = tabs.findIndex((t) => t.key === activeTab);
    return i >= 0 ? i : 0;
  }, [tabs, activeTab]);

  const activeComponent = components?.find(
    (component: IDynamicComponent) => component.symbol === activeTab
  );

  const renderActiveComponent = () => {
    if (activeComponent?.type === 'tablePanel' || activeComponent?.type === 'grid') {
      const wiseTableProps = dynamicComponentToWiseTableProps({
        component: activeComponent,
        pageSymbol,
        requestParams,
        componentParams
      });
      return (
        <div className="tabContent">
          <WiseTable isCondensed showPlainTable {...wiseTableProps} />
        </div>
      );
    }

    if (activeComponent?.type === 'editPanel') {
      return (
        <div className="tabContent">
          <EditPanel
            label={activeComponent.label}
            fields={activeComponent.fields}
            actions={activeComponent.actions}
            isModal={false}
            loadDataParams={activeComponent.load_data_params}
            pageSymbol={pageSymbol}
            componentSymbol={activeComponent.symbol}
            requestParams={requestParams}
          />
        </div>
      );
    }

    if (
      activeComponent?.type === 'listHorizontalPanel' ||
      activeComponent?.type === 'verticalPanel'
    ) {
      return (
        <ListPanel
          pageSymbol={pageSymbol}
          components={activeComponent.components}
          requestParams={requestParams}
          horizontal={activeComponent?.type === 'listHorizontalPanel'}
          componentParams={componentParams}
        />
      );
    }

    return <div className="tabPlaceholder" />;
  };

  return (
    <div className="listWrapper">
      <PageTitle title={title} />
      <Box className="tabPanel-tabsWrapper">
        {tabs.length > 0 ? (
          <Tabs
            value={activeIndex}
            onChange={(_, newValue: number) => {
              const next = tabs[newValue];
              if (next) {
                setActiveTab(next.key);
              }
            }}
            color="secondary"
            textColor="secondary"
            indicatorColor="secondary"
            sx={tabsSx}>
            {tabs.map((tab) => (
              <Tab
                key={String(tab.key)}
                color="secondary"
                label={String(tab.label ?? '').toUpperCase()}
                id={`tabpanel-tab-${tab.key}`}
                aria-controls={`tabpanel-panel-${tab.key}`}
              />
            ))}
          </Tabs>
        ) : null}
      </Box>
      <div
        className="contentWrapper"
        role="tabpanel"
        id={`tabpanel-panel-${activeTab}`}
        aria-labelledby={`tabpanel-tab-${activeTab}`}>
        {renderActiveComponent()}
      </div>
    </div>
  );
};

export default TabPanel;
